/**
 * ContextTab Component
 *
 * First tab of the Investment Editor with 9 sections:
 * 1. Basic Information
 * 2. Regulatory Information
 * 3. Prime Objective
 * 4. Problem & Context
 * 5. Improvement Ideas
 * 6. Assumptions & Constraints
 * 7. Key Milestones
 * 8. Success Measures
 * 9. Sponsors
 */

import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import {
  usePortcos,
  useSegments,
  useMeasureTypes,
  useSponsorTypes,
  useMilestones,
  useCreateMilestone,
  useUpdateMilestone,
  useDeleteMilestone,
  useSponsors,
  useCreateSponsor,
  useUpdateSponsor,
  useDeleteSponsor,
  useSuccessMeasures,
  useCreateSuccessMeasure,
  useUpdateSuccessMeasure,
  useDeleteSuccessMeasure,
  useLatestDecision,
} from '@medsuite/api-client';
import {
  Input,
  Select,
  TextArea,
  MarkdownEditor,
  Checkbox,
  DatePicker,
  StatusBadge,
  Button,
  Card,
  Modal,
  ModalFooter,
} from '@medsuite/ui-components';
import React, { useState, useEffect, useRef } from 'react';

import { UserSearchInput } from '../../../components/forms/UserSearchInput';

import type { GraphUser } from '../../../hooks/useGraphUserSearch';
import type {
  Investment,
  Milestone,
  SuccessMeasure,
  Sponsor,
  Portco,
  Segment,
  MeasureType,
  SponsorType,
} from '@medsuite/models';

interface ContextTabProps {
  investment: Investment;
  onUpdate: (updates: Partial<Investment>) => void;
  isEditing: boolean;
  setIsEditing: (editing: boolean) => void;
  onSave?: () => Promise<void>;
  onRegisterSaveFunctions?: (saveFunctions: {
    saveMilestones: () => Promise<void>;
    saveSponsors: () => Promise<void>;
    saveSuccessMeasures: () => Promise<void>;
  }) => void;
}

/**
 * ContextTab Component
 */
export const ContextTab: React.FC<ContextTabProps> = ({
  investment,
  onUpdate,
  isEditing: _isEditing,
  setIsEditing,
  onRegisterSaveFunctions,
}) => {
  // Local state for form fields
  const [formData, setFormData] = useState({
    title: investment.title || '',
    portco_id: investment.portco_id,
    segment_id: investment.segment_id,
    submitted_date: investment.submitted_date,
    is_regulatory_requirement: investment.is_regulatory_requirement,
    regulatory_details: investment.regulatory_details || '',
    prime_objective: investment.prime_objective || '',
    problem_context: investment.problem_context || '',
    improvement_ideas: investment.improvement_ideas || '',
    assumptions: investment.assumptions || '',
  });

  // Section navigation state
  type SectionId =
    | 'basic-info'
    | 'prime-objective'
    | 'problem-context'
    | 'improvement-ideas'
    | 'assumptions'
    | 'milestones'
    | 'success-measures'
    | 'sponsors';

  const SECTIONS = [
    { id: 'basic-info' as SectionId, label: 'Basic Information' },
    { id: 'prime-objective' as SectionId, label: 'Prime Objective' },
    { id: 'problem-context' as SectionId, label: 'Problem & Context' },
    { id: 'improvement-ideas' as SectionId, label: 'Improvement Ideas' },
    { id: 'assumptions' as SectionId, label: 'Assumptions & Constraints' },
    { id: 'milestones' as SectionId, label: 'Key Milestones' },
    { id: 'success-measures' as SectionId, label: 'Success Measures' },
    { id: 'sponsors' as SectionId, label: 'Sponsors' },
  ];

  const [selectedSection, setSelectedSection] = useState<SectionId>('basic-info');

  // Modal states for Success Measures
  const [successMeasureModalOpen, setSuccessMeasureModalOpen] = useState(false);
  const [editingSuccessMeasureIndex, setEditingSuccessMeasureIndex] = useState<number | null>(null);
  const [successMeasureForm, setSuccessMeasureForm] = useState<Partial<SuccessMeasure>>({});
  const [isSavingSuccessMeasure, setIsSavingSuccessMeasure] = useState(false);

  // Modal states for Sponsors
  const [sponsorModalOpen, setSponsorModalOpen] = useState(false);
  const [editingSponsorIndex, setEditingSponsorIndex] = useState<number | null>(null);
  const [sponsorForm, setSponsorForm] = useState<Partial<Sponsor>>({});
  const [isSavingSponsor, setIsSavingSponsor] = useState(false);

  // Modal states for Milestones
  const [milestoneModalOpen, setMilestoneModalOpen] = useState(false);
  const [editingMilestoneIndex, setEditingMilestoneIndex] = useState<number | null>(null);
  const [milestoneForm, setMilestoneForm] = useState<Partial<Milestone>>({});
  const [isSavingMilestone, setIsSavingMilestone] = useState(false);

  // Help dialog states
  const [helpDialogOpen, setHelpDialogOpen] = useState<string | null>(null);

  // Help text content for each section
  const HELP_TEXT: Record<string, { title: string; content: React.ReactNode }> = {
    'prime-objective': {
      title: 'Prime Objective Help',
      content: (
        <p className="text-sm text-gray-700 dark:text-gray-300">
          Describe in a brief narrative (only a few sentences) what this investment will accomplish
          and what the expected outcome will look like. This is not the problem statement, but the
          desired plan, solution, and resolution once the investment is complete. You can include
          narratives about benefits, how we will approach the investment, and cover the overall
          objective in a way that represents the benefits.
        </p>
      ),
    },
    'problem-context': {
      title: 'Problem & Context Help',
      content: (
        <p className="text-sm text-gray-700 dark:text-gray-300">
          Describe in a brief narrative (only a few sentences) the problem statement and context for
          the need of this investment. What challenges do we face, what strategic roadblocks are in
          the way, how does not doing this affect our business? Keep this section focused
          exclusively on problem and context of the problem, and do not put outcomes or desired
          outcomes in this section.
        </p>
      ),
    },
    'improvement-ideas': {
      title: 'Improvement Ideas Help',
      content: (
        <div className="text-sm text-gray-700 dark:text-gray-300 space-y-3">
          <p>
            This section is typically in outline form and covers the &quot;HOW&quot; of the solution, what
            steps will be executed with the investment, and also how this aligns to the strategic
            goals of the company. Remember you can use the markdown editor to include bullets and
            formatting options.
          </p>
          <div>
            <p className="font-medium mb-1">
              What will we do and the major change made by the investment:
            </p>
            <ul className="list-disc list-inside ml-2 space-y-1">
              <li>What costs will we eliminate?</li>
              <li>How will we leverage those savings to enhance the business?</li>
              <li>What particular step in the change will happen?</li>
              <li>
                How will this step improve the company, process, role, product, platform, market,
                etc.?
              </li>
            </ul>
          </div>
          <div>
            <p className="font-medium mb-1">
              How does this align to our strategic goals as a company?
            </p>
            <ul className="list-disc list-inside ml-2 space-y-1">
              <li>What resources are improved?</li>
              <li>How does this enhance our market reach?</li>
              <li>
                Come up with your own assertions and benefits and strategic components. This is just
                the format expected.
              </li>
            </ul>
          </div>
        </div>
      ),
    },
    assumptions: {
      title: 'Assumptions & Constraints Help',
      content: (
        <div className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
          <p>
            List key assumptions and call out pivotal components of the investment or plan or
            outcome.
          </p>
          <p>
            List key constraints about the investment that could be either additive or detract from
            the results.
          </p>
          <p>
            For example, by not doing this investment, the business will continue to have ______
            result.
          </p>
          <p>Remember you can use bullets and formatting options in the editor.</p>
        </div>
      ),
    },
    milestones: {
      title: 'Key Milestones Help',
      content: (
        <div className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
          <p>
            Make sure to include a project kickoff milestone at the beginning with a proposed date.
          </p>
          <p>Include all major project milestones.</p>
          <p>Make sure to include a planned completion or go-live milestone at the end.</p>
        </div>
      ),
    },
  };

  // Fetch lookup data
  const { data: portcos = [] } = usePortcos();
  const { data: segments = [] } = useSegments();
  const { data: measureTypes = [] } = useMeasureTypes();
  const { data: sponsorTypes = [] } = useSponsorTypes();


  // Fetch investment-specific data
  // Disable automatic refetching to prevent overwriting local edits
  const { data: milestones = [], isSuccess: milestonesSuccess } = useMilestones(
    investment.investment_id,
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }
  );
  const { data: sponsors = [], isSuccess: sponsorsSuccess } = useSponsors(
    investment.investment_id,
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }
  );
  const { data: successMeasures = [], isSuccess: successMeasuresSuccess } = useSuccessMeasures(
    investment.investment_id,
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }
  );

  // Fetch latest decision for status context (for deferred/rejected rationale)
  const { data: latestDecision } = useLatestDecision(investment.investment_id, {
    enabled: ['deferred', 'rejected'].includes(investment.status),
  });

  // Mutation hooks for milestones (disable auto-invalidation)
  const createMilestone = useCreateMilestone(investment.investment_id);
  const updateMilestone = useUpdateMilestone(investment.investment_id);
  const deleteMilestone = useDeleteMilestone(investment.investment_id);

  // Mutation hooks for sponsors (disable auto-invalidation)
  const createSponsor = useCreateSponsor(investment.investment_id);
  const updateSponsor = useUpdateSponsor(investment.investment_id);
  const deleteSponsor = useDeleteSponsor(investment.investment_id);

  // Mutation hooks for success measures (disable auto-invalidation)
  const createSuccessMeasure = useCreateSuccessMeasure(investment.investment_id);
  const updateSuccessMeasure = useUpdateSuccessMeasure(investment.investment_id);
  const deleteSuccessMeasure = useDeleteSuccessMeasure(investment.investment_id);

  // Use refs to persist data across remounts, with state for reactivity
  const milestonesRef = useRef<Milestone[]>([]);
  const sponsorsRef = useRef<Sponsor[]>([]);
  const successMeasuresRef = useRef<SuccessMeasure[]>([]);

  // Track original server data to detect deletions
  const originalMilestonesRef = useRef<Milestone[]>([]);
  const originalSponsorsRef = useRef<Sponsor[]>([]);
  const originalSuccessMeasuresRef = useRef<SuccessMeasure[]>([]);

  const [localMilestones, setLocalMilestones] = useState<Milestone[]>([]);
  const [localSuccessMeasures, setLocalSuccessMeasures] = useState<SuccessMeasure[]>([]);
  const [localSponsors, setLocalSponsors] = useState<Sponsor[]>([]);

  // Wrapper functions that update both ref and state
  const updateLocalMilestones = (
    milestones: Milestone[] | ((prev: Milestone[]) => Milestone[])
  ) => {
    const newValue =
      typeof milestones === 'function' ? milestones(milestonesRef.current) : milestones;
    milestonesRef.current = newValue;
    setLocalMilestones(newValue);
  };

  const updateLocalSponsors = (sponsors: Sponsor[] | ((prev: Sponsor[]) => Sponsor[])) => {
    const newValue = typeof sponsors === 'function' ? sponsors(sponsorsRef.current) : sponsors;
    sponsorsRef.current = newValue;
    setLocalSponsors(newValue);
  };

  const updateLocalSuccessMeasures = (
    measures: SuccessMeasure[] | ((prev: SuccessMeasure[]) => SuccessMeasure[])
  ) => {
    const newValue =
      typeof measures === 'function' ? measures(successMeasuresRef.current) : measures;
    successMeasuresRef.current = newValue;
    setLocalSuccessMeasures(newValue);
  };

  // Track which investment ID we've initialized for
  const initializedInvestmentId = useRef<number | null>(null);
  const milestonesDataLoaded = useRef(false);
  const sponsorsDataLoaded = useRef(false);
  const successMeasuresDataLoaded = useRef(false);

  // Reset when investment ID changes
  useEffect(() => {
    if (initializedInvestmentId.current !== investment.investment_id) {
      initializedInvestmentId.current = investment.investment_id;
      milestonesDataLoaded.current = false;
      sponsorsDataLoaded.current = false;
      successMeasuresDataLoaded.current = false;
      // Clear both refs and state
      milestonesRef.current = [];
      sponsorsRef.current = [];
      successMeasuresRef.current = [];
      originalMilestonesRef.current = [];
      originalSponsorsRef.current = [];
      originalSuccessMeasuresRef.current = [];
      setLocalMilestones([]);
      setLocalSponsors([]);
      setLocalSuccessMeasures([]);
    }
  }, [investment.investment_id]);

  // Load initial milestones data (only once, and only after query succeeds)
  useEffect(() => {
    if (
      !milestonesDataLoaded.current &&
      initializedInvestmentId.current === investment.investment_id &&
      milestonesSuccess
    ) {
      updateLocalMilestones(milestones);
      originalMilestonesRef.current = [...milestones]; // Store original for delete detection
      milestonesDataLoaded.current = true;
    }
  }, [milestones, milestonesSuccess, investment.investment_id]);

  // Load initial sponsors data (only once, and only after query succeeds)
  useEffect(() => {
    if (
      !sponsorsDataLoaded.current &&
      initializedInvestmentId.current === investment.investment_id &&
      sponsorsSuccess
    ) {
      updateLocalSponsors(sponsors);
      originalSponsorsRef.current = [...sponsors]; // Store original for delete detection
      sponsorsDataLoaded.current = true;
    }
  }, [sponsors, sponsorsSuccess, investment.investment_id]);

  // Load initial success measures data (only once, and only after query succeeds)
  useEffect(() => {
    if (
      !successMeasuresDataLoaded.current &&
      initializedInvestmentId.current === investment.investment_id &&
      successMeasuresSuccess
    ) {
      updateLocalSuccessMeasures(successMeasures);
      originalSuccessMeasuresRef.current = [...successMeasures]; // Store original for delete detection
      successMeasuresDataLoaded.current = true;
    }
  }, [successMeasures, successMeasuresSuccess, investment.investment_id]);

  // Handle form field changes
  const handleFieldChange = (field: keyof typeof formData, value: string | number | boolean | null) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setIsEditing(true);
    onUpdate({ [field]: value });
  };

  // Milestones handlers
  const handleOpenMilestoneModal = (index?: number) => {
    if (index !== undefined && index >= 0 && index < localMilestones.length) {
      // Edit existing
      setEditingMilestoneIndex(index);
      const milestone = localMilestones[index];
      setMilestoneForm({
        title: milestone.title,
        expected_date: milestone.expected_date,
      });
    } else {
      // Add new
      setEditingMilestoneIndex(null);
      setMilestoneForm({
        title: '',
        expected_date: new Date(),
      });
    }
    setMilestoneModalOpen(true);
  };

  const handleCloseMilestoneModal = () => {
    setMilestoneModalOpen(false);
    setEditingMilestoneIndex(null);
    setMilestoneForm({});
    setIsSavingMilestone(false);
  };

  const handleSaveMilestone = () => {
    // Prevent double-submission
    if (isSavingMilestone) {
      return;
    }

    setIsSavingMilestone(true);

    if (editingMilestoneIndex !== null) {
      // Update existing
      const updated = [...milestonesRef.current];
      updated[editingMilestoneIndex] = {
        ...updated[editingMilestoneIndex],
        title: milestoneForm.title || '',
        expected_date: milestoneForm.expected_date || new Date(),
      };
      updateLocalMilestones(updated);
    } else {
      // Add new
      const newMilestone: Milestone = {
        milestone_id: 0, // Will be set by server
        investment_id: investment.investment_id,
        sequence: milestonesRef.current.length + 1,
        title: milestoneForm.title || '',
        expected_date: milestoneForm.expected_date || new Date(),
      };
      updateLocalMilestones([...milestonesRef.current, newMilestone]);
    }
    setIsEditing(true);
    handleCloseMilestoneModal();
  };

  const handleDeleteMilestone = (index: number) => {
    updateLocalMilestones(milestonesRef.current.filter((_, i) => i !== index));
    setIsEditing(true);
  };

  const handleMoveMilestone = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= milestonesRef.current.length) {
      return;
    }

    const updated = [...milestonesRef.current];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];

    // Update sequences
    updated.forEach((m, i) => {
      m.sequence = i + 1;
    });

    updateLocalMilestones(updated);
    setIsEditing(true);
  };

  // Success Measures handlers
  const handleOpenSuccessMeasureModal = (index?: number) => {
    if (index !== undefined && index >= 0 && index < localSuccessMeasures.length) {
      // Edit existing
      setEditingSuccessMeasureIndex(index);
      const measure = localSuccessMeasures[index];
      setSuccessMeasureForm({
        measure_type_id: measure.measure_type_id,
        title: measure.title,
        alternate_value: measure.alternate_value || '',
        amount: measure.amount,
        amount_type: measure.amount_type || null,
        period_start: measure.period_start,
        period_end: measure.period_end,
        is_one_time: measure.is_one_time || false,
      });
    } else {
      // Add new
      setEditingSuccessMeasureIndex(null);
      setSuccessMeasureForm({
        measure_type_id: measureTypes[0]?.measure_type_id || 0,
        title: '',
        alternate_value: '',
        amount: null,
        amount_type: null,
        period_start: null,
        period_end: null,
        is_one_time: false,
      });
    }
    setSuccessMeasureModalOpen(true);
  };

  const handleCloseSuccessMeasureModal = () => {
    setSuccessMeasureModalOpen(false);
    setEditingSuccessMeasureIndex(null);
    setSuccessMeasureForm({});
    setIsSavingSuccessMeasure(false);
  };

  const handleSaveSuccessMeasure = () => {
    // Prevent double-submission
    if (isSavingSuccessMeasure) {
      return;
    }

    setIsSavingSuccessMeasure(true);

    if (editingSuccessMeasureIndex !== null) {
      // Update existing
      const updated = [...successMeasuresRef.current];
      updated[editingSuccessMeasureIndex] = {
        ...updated[editingSuccessMeasureIndex],
        ...successMeasureForm,
      };
      updateLocalSuccessMeasures(updated);
    } else {
      // Add new
      const newMeasure: SuccessMeasure = {
        success_measure_id: 0,
        investment_id: investment.investment_id,
        measure_type_id:
          (successMeasureForm.measure_type_id as number) || measureTypes[0]?.measure_type_id || 0,
        title: successMeasureForm.title || '',
        alternate_value: successMeasureForm.alternate_value || null,
        amount: successMeasureForm.amount || null,
        amount_type: (successMeasureForm.amount_type as SuccessMeasure['amount_type']) || null,
        period_start: successMeasureForm.period_start || null,
        period_end: successMeasureForm.period_end || null,
        is_one_time: successMeasureForm.is_one_time || false,
      };
      updateLocalSuccessMeasures([...successMeasuresRef.current, newMeasure]);
    }
    setIsEditing(true);
    handleCloseSuccessMeasureModal();
  };

  const handleDeleteSuccessMeasure = (index: number) => {
    updateLocalSuccessMeasures(successMeasuresRef.current.filter((_, i) => i !== index));
    setIsEditing(true);
  };

  // Sponsors handlers
  const handleOpenSponsorModal = (index?: number) => {
    if (index !== undefined && index >= 0 && index < localSponsors.length) {
      // Edit existing
      setEditingSponsorIndex(index);
      const sponsor = localSponsors[index];
      setSponsorForm({
        sponsor_type_id: sponsor.sponsor_type_id,
        sponsor_name: sponsor.sponsor_name,
        sponsor_title: sponsor.sponsor_title,
        sponsor_email: sponsor.sponsor_email,
        is_research_owner: sponsor.is_research_owner,
      });
    } else {
      // Add new
      setEditingSponsorIndex(null);
      setSponsorForm({
        sponsor_type_id: sponsorTypes[0]?.sponsor_type_id || 0,
        sponsor_name: '',
        sponsor_title: '',
        sponsor_email: '',
        is_research_owner: false,
      });
    }
    setSponsorModalOpen(true);
  };

  const handleCloseSponsorModal = () => {
    setSponsorModalOpen(false);
    setEditingSponsorIndex(null);
    setSponsorForm({});
    setIsSavingSponsor(false);
  };

  const handleSaveSponsor = () => {
    // Prevent double-submission
    if (isSavingSponsor) {
      return;
    }

    setIsSavingSponsor(true);

    if (editingSponsorIndex !== null) {
      // Update existing
      const updated = [...sponsorsRef.current];
      updated[editingSponsorIndex] = {
        ...updated[editingSponsorIndex],
        ...sponsorForm,
      };
      updateLocalSponsors(updated);
    } else {
      // Add new
      const newSponsor: Sponsor = {
        sponsor_id: 0,
        investment_id: investment.investment_id,
        sponsor_type_id:
          (sponsorForm.sponsor_type_id as number) || sponsorTypes[0]?.sponsor_type_id || 0,
        sponsor_name: sponsorForm.sponsor_name || '',
        sponsor_title: sponsorForm.sponsor_title || '',
        sponsor_email: sponsorForm.sponsor_email || '',
        is_research_owner: sponsorForm.is_research_owner || false,
      };
      updateLocalSponsors([...sponsorsRef.current, newSponsor]);
    }
    setIsEditing(true);
    handleCloseSponsorModal();
  };

  const handleDeleteSponsor = (index: number) => {
    updateLocalSponsors(sponsorsRef.current.filter((_, i) => i !== index));
    setIsEditing(true);
  };

  // Save milestones to server
  const saveMilestones = async () => {
    // Use ref to get the latest data (survives remounts)
    const currentMilestones = milestonesRef.current;

    // Find milestones to delete (in original but not in current)
    const currentIds = new Set(
      currentMilestones.map((m) => m.milestone_id).filter((id) => id !== 0)
    );
    const toDelete = originalMilestonesRef.current.filter(
      (m) => m.milestone_id !== 0 && !currentIds.has(m.milestone_id)
    );
    for (const milestone of toDelete) {
      await deleteMilestone.mutateAsync(milestone.milestone_id);
    }

    // Find milestones to create (milestone_id === 0)
    const toCreate = currentMilestones.filter((m) => m.milestone_id === 0);
    const createdMilestones: Milestone[] = [];
    for (const milestone of toCreate) {
      const created = await createMilestone.mutateAsync({
        sequence: milestone.sequence,
        title: milestone.title,
        expected_date: milestone.expected_date,
      });
      createdMilestones.push(created);
    }

    // Find milestones to update (exists in both local and server)
    const toUpdate = currentMilestones.filter((m) => m.milestone_id !== 0);
    for (const milestone of toUpdate) {
      await updateMilestone.mutateAsync({
        milestoneId: milestone.milestone_id,
        data: {
          sequence: milestone.sequence,
          title: milestone.title,
          expected_date: milestone.expected_date,
        },
      });
    }

    // Update local state with newly created milestones that have real IDs
    if (createdMilestones.length > 0) {
      updateLocalMilestones((prev) => {
        // Remove temporary milestones (id === 0) and add the created ones
        const withoutTemp = prev.filter((m) => m.milestone_id !== 0);
        return [...withoutTemp, ...createdMilestones];
      });
    }

    // Update original ref to match current state after save
    originalMilestonesRef.current = [...milestonesRef.current];
  };

  // Save sponsors to server
  const saveSponsors = async () => {
    // Use ref to get the latest data (survives remounts)
    const currentSponsors = sponsorsRef.current;

    // Note: Required sponsor type validation is now handled as a UI warning
    // in the renderSponsors function, not as a blocking save error

    // Find sponsors to delete (in original but not in current)
    const currentIds = new Set(currentSponsors.map((s) => s.sponsor_id).filter((id) => id !== 0));
    const toDelete = originalSponsorsRef.current.filter(
      (s) => s.sponsor_id !== 0 && !currentIds.has(s.sponsor_id)
    );
    for (const sponsor of toDelete) {
      await deleteSponsor.mutateAsync(sponsor.sponsor_id);
    }

    // Find sponsors to create (sponsor_id === 0)
    const toCreate = currentSponsors.filter((s) => s.sponsor_id === 0);
    const createdSponsors: Sponsor[] = [];
    for (const sponsor of toCreate) {
      const created = await createSponsor.mutateAsync({
        sponsor_type_id: sponsor.sponsor_type_id,
        sponsor_name: sponsor.sponsor_name,
        sponsor_title: sponsor.sponsor_title,
        sponsor_email: sponsor.sponsor_email,
        is_research_owner: sponsor.is_research_owner,
      });
      createdSponsors.push(created);
    }

    // Find sponsors to update (exists in both local and server)
    const toUpdate = currentSponsors.filter((s) => s.sponsor_id !== 0);
    for (const sponsor of toUpdate) {
      await updateSponsor.mutateAsync({
        sponsorId: sponsor.sponsor_id,
        data: {
          sponsor_type_id: sponsor.sponsor_type_id,
          sponsor_name: sponsor.sponsor_name,
          sponsor_title: sponsor.sponsor_title,
          sponsor_email: sponsor.sponsor_email,
          is_research_owner: sponsor.is_research_owner,
        },
      });
    }

    // Update local state with newly created sponsors that have real IDs
    if (createdSponsors.length > 0) {
      updateLocalSponsors((prev) => {
        // Remove temporary sponsors (id === 0) and add the created ones
        const withoutTemp = prev.filter((s) => s.sponsor_id !== 0);
        return [...withoutTemp, ...createdSponsors];
      });
    }

    // Update original ref to match current state after save
    originalSponsorsRef.current = [...sponsorsRef.current];
  };

  // Save success measures to server
  const saveSuccessMeasures = async () => {
    // Use ref to get the latest data (survives remounts)
    const currentMeasures = successMeasuresRef.current;

    // Find success measures to delete (in original but not in current)
    const currentIds = new Set(
      currentMeasures.map((m) => m.success_measure_id).filter((id) => id !== 0)
    );
    const toDelete = originalSuccessMeasuresRef.current.filter(
      (m) => m.success_measure_id !== 0 && !currentIds.has(m.success_measure_id)
    );
    for (const measure of toDelete) {
      await deleteSuccessMeasure.mutateAsync(measure.success_measure_id);
    }

    // Find success measures to create (success_measure_id === 0)
    const toCreate = currentMeasures.filter((m) => m.success_measure_id === 0);
    const createdMeasures: SuccessMeasure[] = [];
    for (const measure of toCreate) {
      try {
        // Convert amount to number if it's a string
        const amountValue = measure.amount != null ? Number(measure.amount) : null;

        const created = await createSuccessMeasure.mutateAsync({
          measure_type_id: measure.measure_type_id,
          title: measure.title,
          alternate_value: measure.alternate_value || null,
          amount: amountValue,
          amount_type: measure.amount_type,
          period_start: measure.period_start || null,
          period_end: measure.period_end || null,
          is_one_time: measure.is_one_time,
        });
        createdMeasures.push(created);
      } catch (err) {
        console.error('Failed to create success measure:', measure, err);
        throw err;
      }
    }

    // Find success measures to update (exists in both local and server)
    const toUpdate = currentMeasures.filter((m) => m.success_measure_id !== 0);
    for (const measure of toUpdate) {
      try {
        // Convert amount to number if it's a string (comes from DB as string)
        const amountValue = measure.amount != null ? Number(measure.amount) : null;

        const updateData = {
          measure_type_id: measure.measure_type_id,
          title: measure.title,
          alternate_value: measure.alternate_value || null,
          amount: amountValue,
          amount_type: measure.amount_type,
          period_start: measure.period_start || null,
          period_end: measure.period_end || null,
          is_one_time: measure.is_one_time,
        };
        await updateSuccessMeasure.mutateAsync({
          successMeasureId: measure.success_measure_id,
          data: updateData,
        });
      } catch (err) {
        console.error('Failed to update success measure:', measure.success_measure_id, err);
        throw err;
      }
    }

    // Update local state with newly created measures that have real IDs
    if (createdMeasures.length > 0) {
      updateLocalSuccessMeasures((prev) => {
        // Remove temporary measures (id === 0) and add the created ones
        const withoutTemp = prev.filter((m) => m.success_measure_id !== 0);
        return [...withoutTemp, ...createdMeasures];
      });
    }

    // Update original ref to match current state after save
    originalSuccessMeasuresRef.current = [...successMeasuresRef.current];
  };

  // Register save functions with parent
  useEffect(() => {
    if (onRegisterSaveFunctions) {
      onRegisterSaveFunctions({
        saveMilestones,
        saveSponsors,
        saveSuccessMeasures,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Register save functions once; they are stable refs and don't need to re-register
  }, [onRegisterSaveFunctions]);

  // Convert lookup data to select options (sorted alphabetically)
  const portcoOptions = portcos
    .map((p: Portco) => ({
      value: p.portco_id,
      label: p.portco_name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const segmentOptions = segments
    .map((s: Segment) => ({
      value: s.segment_id,
      label: s.segment_name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  // Only show benefit types ('B') for success measures
  const measureTypeOptions = measureTypes
    .filter((mt: MeasureType) => mt.cost_or_benefit === 'B')
    .map((mt: MeasureType) => ({
      value: mt.measure_type_id,
      label: mt.measure_type_name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const sponsorTypeOptions = sponsorTypes.map((st: SponsorType) => ({
    value: st.sponsor_type_id,
    label: st.sponsor_type_name,
  }));

  const amountTypeOptions = [
    { value: 'Y', label: 'Days' },
    { value: 'D', label: 'Dollar' },
    { value: 'H', label: 'Headcount' },
    { value: 'R', label: 'Hours' },
    { value: 'M', label: 'Months' },
    { value: 'P', label: 'Percent' },
    { value: 'U', label: 'Units' },
    { value: 'W', label: 'Weeks' },
  ];

  // Milestones Modal
  const renderMilestoneModal = () => {
    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      handleSaveMilestone();
    };

    return (
      <Modal
        isOpen={milestoneModalOpen}
        onClose={handleCloseMilestoneModal}
        title={editingMilestoneIndex !== null ? 'Edit Milestone' : 'Add Milestone'}
        size="md"
      >
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <Input
              label="Title"
              value={milestoneForm.title || ''}
              onChange={(e) => setMilestoneForm({ ...milestoneForm, title: e.target.value })}
              placeholder="Milestone title"
              required
            />

            <DatePicker
              label="Expected Date"
              value={
                milestoneForm.expected_date
                  ? new Date(milestoneForm.expected_date).toISOString().split('T')[0]
                  : ''
              }
              onChange={(e) =>
                setMilestoneForm({
                  ...milestoneForm,
                  expected_date: e.target.value ? new Date(e.target.value) : undefined,
                })
              }
              required
            />
          </div>

          <ModalFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={handleCloseMilestoneModal}
              disabled={isSavingMilestone}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={isSavingMilestone}>
              {isSavingMilestone
                ? 'Saving...'
                : editingMilestoneIndex !== null
                  ? 'Save Changes'
                  : 'Add Milestone'}
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    );
  };

  // Success Measures Modal
  const renderSuccessMeasureModal = () => {
    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      handleSaveSuccessMeasure();
    };

    return (
      <Modal
        isOpen={successMeasureModalOpen}
        onClose={handleCloseSuccessMeasureModal}
        title={editingSuccessMeasureIndex !== null ? 'Edit Success Measure' : 'Add Success Measure'}
        size="lg"
      >
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <Input
              label="Title"
              value={successMeasureForm.title || ''}
              onChange={(e) =>
                setSuccessMeasureForm({ ...successMeasureForm, title: e.target.value })
              }
              placeholder="Success measure title"
              required
            />

            <Select
              label="Measure Type"
              options={measureTypeOptions}
              value={successMeasureForm.measure_type_id || 0}
              onChange={(value) =>
                setSuccessMeasureForm({ ...successMeasureForm, measure_type_id: value as number })
              }
              required
            />

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Amount"
                type="number"
                value={successMeasureForm.amount || ''}
                onChange={(e) =>
                  setSuccessMeasureForm({
                    ...successMeasureForm,
                    amount: parseFloat(e.target.value) || null,
                  })
                }
                placeholder="Amount"
              />

              <Select
                label="Amount Type"
                options={amountTypeOptions}
                value={successMeasureForm.amount_type || ''}
                onChange={(value) =>
                  setSuccessMeasureForm({ ...successMeasureForm, amount_type: value as SuccessMeasure['amount_type'] })
                }
                placeholder="Select type"
              />
            </div>

            <Input
              label="Alternate Value"
              value={successMeasureForm.alternate_value || ''}
              onChange={(e) =>
                setSuccessMeasureForm({ ...successMeasureForm, alternate_value: e.target.value })
              }
              placeholder="Alternative description"
            />

            <div className="grid grid-cols-2 gap-4">
              <DatePicker
                label="Period Start"
                value={
                  successMeasureForm.period_start
                    ? new Date(successMeasureForm.period_start).toISOString().split('T')[0]
                    : ''
                }
                onChange={(e) =>
                  setSuccessMeasureForm({
                    ...successMeasureForm,
                    period_start: e.target.value ? new Date(e.target.value) : null,
                  })
                }
              />

              <DatePicker
                label="Period End"
                value={
                  successMeasureForm.period_end
                    ? new Date(successMeasureForm.period_end).toISOString().split('T')[0]
                    : ''
                }
                onChange={(e) =>
                  setSuccessMeasureForm({
                    ...successMeasureForm,
                    period_end: e.target.value ? new Date(e.target.value) : null,
                  })
                }
              />
            </div>

            <Checkbox
              label="One-Time Measure"
              checked={successMeasureForm.is_one_time || false}
              onChange={(e) =>
                setSuccessMeasureForm({ ...successMeasureForm, is_one_time: e.target.checked })
              }
            />
          </div>

          <ModalFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={handleCloseSuccessMeasureModal}
              disabled={isSavingSuccessMeasure}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={isSavingSuccessMeasure}>
              {isSavingSuccessMeasure
                ? 'Saving...'
                : editingSuccessMeasureIndex !== null
                  ? 'Save Changes'
                  : 'Add Measure'}
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    );
  };

  // Sponsors Modal
  const renderSponsorModal = () => {
    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      handleSaveSponsor();
    };

    return (
      <Modal
        isOpen={sponsorModalOpen}
        onClose={handleCloseSponsorModal}
        title={editingSponsorIndex !== null ? 'Edit Sponsor' : 'Add Sponsor'}
        size="md"
      >
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <Select
              label="Sponsor Type"
              options={sponsorTypeOptions}
              value={sponsorForm.sponsor_type_id || 0}
              onChange={(value) =>
                setSponsorForm({ ...sponsorForm, sponsor_type_id: value as number })
              }
              required
            />

            <UserSearchInput
              label="Name"
              value={sponsorForm.sponsor_name || ''}
              placeholder="Search by name or email..."
              required
              onUserSelect={(user: GraphUser) => {
                setSponsorForm({
                  ...sponsorForm,
                  sponsor_name: user.displayName,
                  sponsor_email: (user.mail || user.userPrincipalName).toLowerCase(),
                  sponsor_title: user.jobTitle || sponsorForm.sponsor_title || '',
                });
              }}
              onInputChange={(value) => setSponsorForm({ ...sponsorForm, sponsor_name: value })}
            />

            <Input
              label="Title"
              value={sponsorForm.sponsor_title || ''}
              onChange={(e) => setSponsorForm({ ...sponsorForm, sponsor_title: e.target.value })}
              placeholder="Job title"
              required
            />

            <Input
              label="Email"
              type="email"
              value={sponsorForm.sponsor_email || ''}
              onChange={(e) => setSponsorForm({ ...sponsorForm, sponsor_email: e.target.value })}
              placeholder="email@example.com"
              required
            />

            <Checkbox
              label="Research Owner"
              checked={sponsorForm.is_research_owner || false}
              onChange={(e) =>
                setSponsorForm({ ...sponsorForm, is_research_owner: e.target.checked })
              }
            />
          </div>

          <ModalFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={handleCloseSponsorModal}
              disabled={isSavingSponsor}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={isSavingSponsor}>
              {isSavingSponsor
                ? 'Saving...'
                : editingSponsorIndex !== null
                  ? 'Save Changes'
                  : 'Add Sponsor'}
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    );
  };

  // Helper to render section header with help icon
  const renderSectionHeader = (title: string, helpKey?: string) => (
    <div className="flex items-center gap-2 mb-4">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
      {helpKey && HELP_TEXT[helpKey] && (
        <button
          type="button"
          onClick={() => setHelpDialogOpen(helpKey)}
          className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
          title="Click for help"
        >
          <QuestionMarkCircleIcon className="w-5 h-5" />
        </button>
      )}
    </div>
  );

  // Help dialog modal
  const renderHelpDialog = () => {
    if (!helpDialogOpen || !HELP_TEXT[helpDialogOpen]) {
      return null;
    }

    const { title, content } = HELP_TEXT[helpDialogOpen];

    return (
      <Modal
        isOpen={!!helpDialogOpen}
        onClose={() => setHelpDialogOpen(null)}
        title={title}
        size="md"
      >
        <div className="py-2">{content}</div>
        <ModalFooter>
          <Button variant="primary" onClick={() => setHelpDialogOpen(null)}>
            Got it
          </Button>
        </ModalFooter>
      </Modal>
    );
  };

  // Section render functions
  const renderBasicInformation = () => (
    <Card>
      <div className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 dark:text-gray-100 mb-4">
          Basic Information
        </h3>
        <div className="space-y-4">
          <Input
            label="Title"
            value={formData.title}
            onChange={(e) => handleFieldChange('title', e.target.value)}
            placeholder="Enter investment title"
            required
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Portfolio Company"
              options={portcoOptions}
              value={formData.portco_id}
              onChange={(value) => handleFieldChange('portco_id', value)}
              placeholder="Select portfolio company"
              required
            />

            <Select
              label="Segment"
              options={segmentOptions}
              value={formData.segment_id}
              onChange={(value) => handleFieldChange('segment_id', value)}
              placeholder="Select segment"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DatePicker
              label="Submitted Date"
              value={
                formData.submitted_date
                  ? new Date(formData.submitted_date).toISOString().split('T')[0] || ''
                  : ''
              }
              onChange={(e) =>
                handleFieldChange(
                  'submitted_date',
                  e.target.value ? new Date(e.target.value).toISOString() : null
                )
              }
              disabled={!!investment.submitted_date}
            />

            <div className="flex flex-col">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Status
              </label>
              <div className="flex items-center h-10">
                <StatusBadge status={investment.status} />
              </div>
              {/* Status contextual help text */}
              <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                {investment.status === 'new' && (
                  <p>
                    When you have completed editing this investment and you are ready for it to be
                    reviewed, change the status to &apos;Submitted&apos;.
                  </p>
                )}
                {investment.status === 'submitted' && (
                  <p>
                    The PGC will review this investment at the next working committee meeting. Check
                    the decision log for details.
                  </p>
                )}
                {investment.status === 'approved' && (
                  <p>
                    This investment has been approved by the PGC and is ready to be planned. Check
                    the decision log for details.
                  </p>
                )}
                {investment.status === 'deferred' && (
                  <p>
                    This investment has been deferred and can be revisited in the future.
                    {latestDecision?.reconsider_date && (
                      <>
                        {' '}
                        Reconsider date:{' '}
                        {new Date(latestDecision.reconsider_date).toLocaleDateString()}.
                      </>
                    )}
                    {latestDecision?.decision_rationale && (
                      <> Rationale: {latestDecision.decision_rationale}</>
                    )}{' '}
                    Check the decision log for details.
                  </p>
                )}
                {investment.status === 'rejected' && (
                  <p>
                    This investment has been rejected by the PGC.
                    {latestDecision?.decision_rationale && (
                      <> The rationale: {latestDecision.decision_rationale}</>
                    )}{' '}
                    Check the decision log for details.
                  </p>
                )}
                {investment.status === 'planned' && (
                  <p>
                    This investment is planned for execution and should be on the product roadmap.
                    Check the decision log for details.
                  </p>
                )}
                {investment.status === 'in_progress' && (
                  <p>
                    Check the product roadmap and the project management leaders for the current
                    status. Check the decision log for details.
                  </p>
                )}
                {investment.status === 'complete' && (
                  <p>This investment has been completed. Check the decision log for details.</p>
                )}
                {investment.status === 'cancelled' && (
                  <p>This investment has been cancelled. Check the decision log for details.</p>
                )}
              </div>
            </div>
          </div>

          {/* Regulatory Information (merged from Section 2) */}
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <h4 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-4">
              Regulatory Information
            </h4>
            <Checkbox
              label="Is this a regulatory requirement?"
              checked={formData.is_regulatory_requirement}
              onChange={(e) => handleFieldChange('is_regulatory_requirement', e.target.checked)}
            />

            {formData.is_regulatory_requirement && (
              <TextArea
                label="Regulatory Details"
                value={formData.regulatory_details}
                onChange={(e) => handleFieldChange('regulatory_details', e.target.value)}
                placeholder="Describe the regulatory requirements..."
                rows={4}
                className="mt-4"
              />
            )}
          </div>
        </div>
      </div>
    </Card>
  );

  const renderPrimeObjective = () => (
    <Card>
      <div className="p-6">
        {renderSectionHeader('Prime Objective', 'prime-objective')}
        <MarkdownEditor
          label="Prime Objective"
          value={formData.prime_objective}
          onChange={(value) => handleFieldChange('prime_objective', value)}
          placeholder="What is the primary objective of this investment?"
          rows={4}
          helperText="Supports markdown formatting (bold, lists, etc.)"
        />
      </div>
    </Card>
  );

  const renderProblemContext = () => (
    <Card>
      <div className="p-6">
        {renderSectionHeader('Problem & Context', 'problem-context')}
        <MarkdownEditor
          label="Problem Context"
          value={formData.problem_context}
          onChange={(value) => handleFieldChange('problem_context', value)}
          placeholder="Describe the problem and context..."
          rows={6}
          helperText="Supports markdown formatting"
        />
      </div>
    </Card>
  );

  const renderImprovementIdeas = () => (
    <Card>
      <div className="p-6">
        {renderSectionHeader('Improvement Ideas', 'improvement-ideas')}
        <MarkdownEditor
          label="Improvement Ideas"
          value={formData.improvement_ideas}
          onChange={(value) => handleFieldChange('improvement_ideas', value)}
          placeholder="What are the proposed improvements?"
          rows={6}
          helperText="Supports markdown formatting"
        />
      </div>
    </Card>
  );

  const renderAssumptions = () => (
    <Card>
      <div className="p-6">
        {renderSectionHeader('Assumptions & Constraints', 'assumptions')}
        <MarkdownEditor
          label="Assumptions & Constraints"
          value={formData.assumptions}
          onChange={(value) => handleFieldChange('assumptions', value)}
          placeholder="List key assumptions and constraints..."
          rows={6}
          helperText="Supports markdown formatting"
        />
      </div>
    </Card>
  );

  const renderMilestones = () => {
    // Helper to format date
    const formatDate = (date: Date | null | undefined) => {
      if (!date) {
        return '-';
      }
      return new Date(date).toLocaleDateString();
    };

    return (
      <>
        {renderMilestoneModal()}
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Key Milestones
                </h3>
                <button
                  type="button"
                  onClick={() => setHelpDialogOpen('milestones')}
                  className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
                  title="Click for help"
                >
                  <QuestionMarkCircleIcon className="w-5 h-5" />
                </button>
              </div>
              <Button type="button" onClick={() => handleOpenMilestoneModal()} size="sm">
                + Add Milestone
              </Button>
            </div>

            {localMilestones.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                No milestones yet. Click &quot;Add Milestone&quot; to get started.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Seq
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Title
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Expected Date
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                    {localMilestones.map((milestone, index) => (
                      <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                        <td className="px-3 py-4 whitespace-nowrap">
                          <div className="flex flex-col gap-1">
                            <button
                              type="button"
                              onClick={() => handleMoveMilestone(index, 'up')}
                              disabled={index === 0}
                              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-30"
                            >
                              ↑
                            </button>
                            <span className="text-sm text-gray-900 dark:text-gray-100">
                              {milestone.sequence}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleMoveMilestone(index, 'down')}
                              disabled={index === localMilestones.length - 1}
                              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-30"
                            >
                              ↓
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                          {milestone.title || '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                          {formatDate(milestone.expected_date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm space-x-2">
                          <Button
                            type="button"
                            onClick={() => handleOpenMilestoneModal(index)}
                            variant="ghost"
                            size="sm"
                            className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300"
                          >
                            Edit
                          </Button>
                          <Button
                            type="button"
                            onClick={() => handleDeleteMilestone(index)}
                            variant="ghost"
                            size="sm"
                            className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Card>
      </>
    );
  };

  const renderSuccessMeasures = () => {
    // Helper to get measure type name
    const getMeasureTypeName = (id: number) => {
      return measureTypes.find((mt) => mt.measure_type_id === id)?.measure_type_name || 'Unknown';
    };

    // Helper to format amount with proper type display
    const formatMeasureAmount = (amount: number | null, amountType: string | null): string => {
      if (amount === null || amount === undefined) {
        return '-';
      }
      const formattedNumber = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(amount);

      switch (amountType) {
        case 'D':
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }).format(amount);
        case 'P':
          return `${formattedNumber}%`;
        case 'Y':
          return `${formattedNumber} Days`;
        case 'H':
          return `${formattedNumber} Headcount`;
        case 'M':
          return `${formattedNumber} Months`;
        case 'R':
          return `${formattedNumber} Hours`;
        case 'U':
          return `${formattedNumber} Units`;
        case 'W':
          return `${formattedNumber} Weeks`;
        default:
          return String(amount);
      }
    };

    // Helper to format date
    const formatDate = (date: Date | null) => {
      if (!date) {
        return '-';
      }
      return new Date(date).toLocaleDateString();
    };

    return (
      <>
        {renderSuccessMeasureModal()}
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Success Measures
              </h3>
              <Button type="button" onClick={() => handleOpenSuccessMeasureModal()} size="sm">
                + Add Success Measure
              </Button>
            </div>

            {localSuccessMeasures.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                No success measures yet. Click &quot;Add Success Measure&quot; to get started.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Title
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Measure Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Period
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        One-Time
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                    {localSuccessMeasures.map((measure, index) => (
                      <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                          {measure.title || '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                          {getMeasureTypeName(measure.measure_type_id)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                          {formatMeasureAmount(measure.amount, measure.amount_type)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                          {formatDate(measure.period_start)} - {formatDate(measure.period_end)}
                        </td>
                        <td className="px-6 py-4 text-center text-sm text-gray-900 dark:text-gray-100">
                          {measure.is_one_time ? '✓' : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm space-x-2">
                          <Button
                            type="button"
                            onClick={() => handleOpenSuccessMeasureModal(index)}
                            variant="ghost"
                            size="sm"
                            className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300"
                          >
                            Edit
                          </Button>
                          <Button
                            type="button"
                            onClick={() => handleDeleteSuccessMeasure(index)}
                            variant="ghost"
                            size="sm"
                            className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Card>
      </>
    );
  };

  const renderSponsors = () => {
    // Helper to get sponsor type name
    const getSponsorTypeName = (id: number) => {
      return sponsorTypes.find((st) => st.sponsor_type_id === id)?.sponsor_type_name || 'Unknown';
    };

    // Calculate missing required sponsor types for warning display
    const requiredSponsorTypes = sponsorTypes.filter((st) => st.is_required);
    const existingSponsorTypeIds = new Set(localSponsors.map((s) => s.sponsor_type_id));
    const missingRequiredTypes = requiredSponsorTypes.filter(
      (st) => !existingSponsorTypeIds.has(st.sponsor_type_id)
    );

    return (
      <>
        {renderSponsorModal()}
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Sponsors</h3>
              <Button type="button" onClick={() => handleOpenSponsorModal()} size="sm">
                + Add Sponsor
              </Button>
            </div>

            {/* Warning banner for missing required sponsor types */}
            {missingRequiredTypes.length > 0 && (
              <div className="mb-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
                <div className="flex items-start">
                  <svg
                    className="h-5 w-5 text-amber-400 mt-0.5 mr-3 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                      Missing Required Sponsor Types
                    </p>
                    <p className="mt-1 text-sm text-amber-700 dark:text-amber-300">
                      The following sponsor types are required:{' '}
                      <span className="font-semibold">
                        {missingRequiredTypes.map((st) => st.sponsor_type_name).join(', ')}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            )}

            {localSponsors.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                No sponsors yet. Click &quot;Add Sponsor&quot; to get started.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Sponsor Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Title
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Research Owner
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                    {localSponsors.map((sponsor, index) => (
                      <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                          {getSponsorTypeName(sponsor.sponsor_type_id)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                          {sponsor.sponsor_name || '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                          {sponsor.sponsor_title || '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                          {sponsor.sponsor_email || '-'}
                        </td>
                        <td className="px-6 py-4 text-center text-sm">
                          {sponsor.is_research_owner ? (
                            <span className="inline-flex items-center rounded-full bg-green-100 dark:bg-green-900/50 px-2 py-0.5 text-xs font-medium text-green-700 dark:text-green-300">
                              Yes
                            </span>
                          ) : null}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm space-x-2">
                          <Button
                            type="button"
                            onClick={() => handleOpenSponsorModal(index)}
                            variant="ghost"
                            size="sm"
                            className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300"
                          >
                            Edit
                          </Button>
                          <Button
                            type="button"
                            onClick={() => handleDeleteSponsor(index)}
                            variant="ghost"
                            size="sm"
                            className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Card>
      </>
    );
  };

  const renderSelectedSection = () => {
    switch (selectedSection) {
      case 'basic-info':
        return renderBasicInformation();
      case 'prime-objective':
        return renderPrimeObjective();
      case 'problem-context':
        return renderProblemContext();
      case 'improvement-ideas':
        return renderImprovementIdeas();
      case 'assumptions':
        return renderAssumptions();
      case 'milestones':
        return renderMilestones();
      case 'success-measures':
        return renderSuccessMeasures();
      case 'sponsors':
        return renderSponsors();
      default:
        return null;
    }
  };

  return (
    <div className="flex h-full bg-gray-50 dark:bg-gray-900">
      {/* Sidebar */}
      <aside className="w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 overflow-y-auto">
        <nav className="p-4 space-y-1">
          {SECTIONS.map((section) => (
            <button
              key={section.id}
              onClick={() => setSelectedSection(section.id)}
              className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                selectedSection === section.id
                  ? 'bg-indigo-50 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 font-medium'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              {section.label}
            </button>
          ))}
        </nav>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto p-6 bg-gray-50 dark:bg-gray-900">
        {renderSelectedSection()}
      </main>

      {/* Help Dialog */}
      {renderHelpDialog()}
    </div>
  );
};

export default ContextTab;
